import { AxiosHttpClient } from "./AxiosHttpService";

export interface ExportRequestDto {
  id: string;
  userId: string;
  status: "PENDING" | "RUNNING" | "DONE" | "FAILED";
  format: "ZIP" | "JSON";
  period: "ALL" | "LAST_YEAR" | "LAST_MONTH";
  fileName?: string;
  fileSize?: number;
  createdAt: string;
  finishedAt?: string;
  fileUrl?: string;
  error?: string;
}

export interface ExportRequestCreateDto {
  format: "ZIP" | "JSON";
  period: "ALL" | "LAST_YEAR" | "LAST_MONTH";
}

export class ExportService {
  private static readonly BASE_URL = "api/v1/docuveritas/exports";

  /**
   * Récupère tous les exports de l'utilisateur connecté
   */
  static async getUserExports(): Promise<ExportRequestDto[]> {
    return AxiosHttpClient.get<ExportRequestDto[]>(this.BASE_URL);
  }

  /**
   * Crée une nouvelle demande d'export
   */
  static async createExport(
    request: ExportRequestCreateDto
  ): Promise<ExportRequestDto> {
    return AxiosHttpClient.post<ExportRequestDto>(this.BASE_URL, request);
  }

  /**
   * Télécharge un export spécifique
   */
  static async downloadExport(
    exportId: string,
    fileName: string
  ): Promise<void> {
    try {
      const blob = await AxiosHttpClient.getBlob(
        `${this.BASE_URL}/${exportId}/download`
      );

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || `export-${exportId}.zip`;
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors du téléchargement de l'export:", error);
      throw error;
    }
  }

  /**
   * Formate la date d'un export
   */
  static formatExportDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /**
   * Formate la taille d'un fichier
   */
  static formatFileSize(bytes?: number): string {
    if (!bytes) return "—";
    
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + " " + sizes[i];
  }

  /**
   * Retourne le libellé d'un statut
   */
  static getStatusLabel(status: ExportRequestDto["status"]): string {
    const labels = {
      PENDING: "En attente",
      RUNNING: "En cours",
      DONE: "Terminé",
      FAILED: "Échec",
    };
    return labels[status];
  }

  /**
   * Retourne le libellé d'une période
   */
  static getPeriodLabel(period: ExportRequestDto["period"]): string {
    const labels = {
      ALL: "Toutes les données",
      LAST_YEAR: "Dernière année",
      LAST_MONTH: "Dernier mois",
    };
    return labels[period];
  }
}
